// HomePage — Energiestroom + KPIs + Charts (was inline in App.tsx vóór H9-PR2)
//
// Pure layout/composition: alle data wordt via props ingegoten zodat polling
// + auth-gate in App.tsx blijft. Geen state intern, geen fetches — dat zou
// dubbele polling veroorzaken naast App.tsx.
//
// PR-2 refactor (audit-2026-05-13 H9): we hebben hier ALLEEN de jsx-body
// verhuisd, geen logic-veranderingen. Renderdiff vs pre-PR2 commit zou nul
// moeten zijn.

import { useMemo, type ReactElement } from 'react';
import { Panel } from '../components/layout/Panel';
import { FlowSVG } from '../components/flow/FlowSVG';
import { KpiColumn } from '../components/kpi/KpiColumn';
import { CombinedSavingsTile } from '../components/kpi/CombinedSavingsTile';
import { EigenverbruikDuoTile } from '../components/kpi/EigenverbruikDuoTile';
import { directPvToLoadTodayKwh } from '../lib/eigenverbruik';
import { MorgenVerwachtTile } from '../components/kpi/MorgenVerwachtTile';
import { SlimmeActieTile } from '../components/kpi/SlimmeActieTile';
import { PriceChart } from '../components/charts/PriceChart';
import { TrendChart } from '../components/charts/TrendChart';
import { CombinedChart } from '../components/charts/CombinedChart';
import { ChartSwitcher } from '../components/charts/ChartSwitcher';
import { PriceLegend, TrendLegend, CombinedLegend } from '../components/charts/legends';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { ControlsColumn } from '../components/controls/ControlsColumn';
import { PvDakTile } from '../components/kpi/PvDakTile';
import { computeSavings } from '../lib/savings';
import { dagverbruikKwh } from '../lib/derive';
import { assembleTimeline } from '../lib/timeline';
import { systemStatusView } from '../lib/systemStatus';
import type {
  Snapshot,
  HealthResponse,
  HistoryResponse,
  PricesResponse,
  PvForecastResponse,
  SocForecastResponse,
  BmsConditioningResponse,
  ReserveLock,
  PvAwareFill,
} from '../api/types';
import type { SavingsTotal } from '../api/savings';
import type { Installation } from '../api/installation';

// Mapping pv_aware_fill.reason → Nederlandse uitleg voor tooltip.
// Sluit aan op planner._is_pv_fill_incentive_active return-strings.
const PV_FILL_REASON_NL: Record<string, string> = {
  profitable_no_deeper_tomorrow: 'profitable spread vandaag + morgen niet goedkoper',
  no_profitable_spread: 'spread vandaag dekt cycling-cost niet',
  tomorrow_deeper_dip: 'morgen heeft goedkoper dal — wacht op morgen',
  insufficient_prices: 'te weinig prijsuren beschikbaar',
  incomplete_today: 'vandaag heeft incomplete prijsdata',
};

/**
 * Render een pill voor de planner pv_aware_fill state.
 *  - active=true → groen "vol-laden modus"
 *  - reason='tomorrow_deeper_dip' → grijs "wacht op morgen (goedkoper)"
 *  - reason='no_profitable_spread' → niets (default state)
 *  - andere reasons → niets (insufficient data — meld niet aan operator)
 *
 * Tooltip toont saturation_raw → saturation_damped (pct) + reden.
 */
export function renderPvFillBadge(fill: PvAwareFill | null): ReactElement | null {
  if (!fill) return null;
  const reasonNl = PV_FILL_REASON_NL[fill.reason] ?? fill.reason;
  const dampPct = Math.round(fill.damp_factor * 100);
  const tooltip =
    `Saturation ${fill.saturation_raw.toFixed(3)} → ` +
    `${fill.saturation_damped.toFixed(3)} (${dampPct}%)\n` +
    `Reden: ${reasonNl}`;

  if (fill.active) {
    return (
      <span
        className="pill pill--ok"
        title={tooltip}
        data-testid="pv-fill-pill-active"
      >
        📈 vol-laden modus
      </span>
    );
  }
  if (fill.reason === 'tomorrow_deeper_dip') {
    return (
      <span
        className="pill pill--neutral"
        title={tooltip}
        data-testid="pv-fill-pill-wait"
      >
        💤 wacht op morgen (goedkoper)
      </span>
    );
  }
  return null;
}

interface HomePageProps {
  snap: Snapshot | null;
  hist: HistoryResponse | null;
  health: HealthResponse | null;
  /** Laatste health-poll mislukt of te oud (#202). `health` is dan null en de
   *  statustegel zegt dat hij het niet weet in plaats van "Optimaal". */
  healthStale?: boolean;
  pvF: PvForecastResponse | null;
  socF: SocForecastResponse | null;
  /** /api/bms-conditioning; null/afwezig = geen conditioning-band. */
  bmsCond?: BmsConditioningResponse | null;
  prices: PricesResponse | null;
  savingsTotal: SavingsTotal | null;
  install: Installation | null;
  reserveLock: ReserveLock | null;
  /**
   * Adaptive-horizon "Nu"-positie. Wordt door App.tsx berekend uit de laatste
   * forecast-end-times (prices/socForecast/pvForecast) en shift de Now-marker
   * naar rechts wanneer de forecast korter is dan 24h — zo wordt het lege
   * rechter chart-gedeelte ingewisseld voor meer history.
   */
  nowIdx?: number;
  onLogout: () => void;
  /**
   * Admin-rol uit /api/auth/status. De homepage is de enige route die een
   * niet-admin te zien krijgt, dus de rol moet hier doorgegeven worden: de
   * ControlsColumn gebruikt hem om het starten van een force-actie af te
   * schermen (punt 63). Zie ControlsColumnProps.isAdmin.
   */
  isAdmin?: boolean;
}

export function HomePage({
  snap,
  hist,
  health,
  healthStale = false,
  pvF,
  socF,
  bmsCond = null,
  prices,
  savingsTotal,
  install,
  reserveLock,
  nowIdx,
  onLogout,
  isAdmin,
}: HomePageProps) {
  // 'now' anchored to the latest snapshot tick — voorkomt jitter in charts
  // wanneer polling-fetches in verschillende tick-momenten landen.
  const now = useMemo(() => new Date(), [snap?._db_ts]); // eslint-disable-line react-hooks/exhaustive-deps
  const status = systemStatusView(health, healthStale);
  const tl = useMemo(
    () => assembleTimeline(now, hist, prices, pvF, socF, nowIdx, bmsCond),
    [now, hist, prices, pvF, socF, nowIdx, bmsCond],
  );

  const savings = hist?.samples ? computeSavings(hist.samples) : null;

  // PV totaal vandaag — gebruik site-totaal (_pv_total_today_kwh) op master-slave,
  // val terug op master-only (pv_today_kwh) voor single-inverter.
  const pvTotalKwh = snap
    ? (snap._pv_total_today_kwh ?? snap.pv_today_kwh ?? 0)
    : 0;
  const pvExportKwh = snap?.grid_export_today_kwh ?? 0;

  // Load-totaal vandaag uit teller 33179; waarom niet household + backup staat
  // bij `dagverbruikKwh` (wachtrij #201).
  const totalLoadKwh = dagverbruikKwh(snap);
  // Directe PV→verbruik per 5-min bucket sinds NL-middernacht (wachtrij #492).
  // De historie beslaat altijd ≥ 24 uur, dus vandaag zit er volledig in.
  const pvDirectToLoadKwh = directPvToLoadTodayKwh(hist?.samples, now);

  // PV-projectie vandaag = al gerealiseerd (inverter-rapport) + forecast voor
  // de resterende uren van vandaag. pv_forecast endpoint geeft alleen toekomstige
  // uren — pure som zou ochtenduren (00-now) missen. Bram-feedback 2026-05-16:
  // "Verwacht <40 kWh terwijl werkelijk al 166" → daar waar de bug zat.
  const todayKwhForecastRemaining = pvF?.hours
    ? pvF.hours
        .filter((h) => {
          const d = new Date(h.hour_start);
          const today = new Date();
          return d.toDateString() === today.toDateString();
        })
        .reduce((sum, h) => sum + (h.pv_kwh ?? 0), 0)
    : null;
  const pvTodayRealisedKwh =
    snap?._pv_total_today_kwh ?? snap?.pv_today_kwh ?? null;
  const todayKwhProjected =
    todayKwhForecastRemaining != null && pvTodayRealisedKwh != null
      ? pvTodayRealisedKwh + todayKwhForecastRemaining
      : todayKwhForecastRemaining;

  // R&D cycle #15 ronde 3: planner pv_aware_fill observability.
  // Pill toont wanneer DP-terminal-saturation gedempt is (= 'vol-laden modus')
  // of expliciet uit-staat omdat morgen goedkoper is. 'no_profitable_spread' =
  // niets te tonen (default state, geen pill).
  const pvFillBadge = renderPvFillBadge(socF?.pv_aware_fill ?? null);

  return (
    <>
      <div className="hero-row">
        <Panel className="panel-flow">
          <ErrorBoundary label="Energiestroom">
            <FlowSVG
              snapshot={snap}
              lastUpdateIso={snap?._db_ts as string | undefined}
              onLogout={onLogout}
              topology={install?.topology ?? null}
              geenEigenPv={install ? install.pv_kwp_nominal === 0 : !!snap?.remote_pv}
            />
          </ErrorBoundary>
        </Panel>

        {/* Improvement #4 (2026-05-16): hero-row is nu 3 kolommen (was 4).
            KPI-tiles zijn samengevoegd in één KpiColumn (was 2×5 = twee kolommen).
            Controls-paneel heeft 440px en gebruikt intern een 2×3 grid:
              Rij 1: EMS-modus | Strategie | Force-actie  (prioritaire controls)
              Rij 2: EV-mode   | Backup    | Vacation     (planning controls)
            Alle 10 KPI-tiles blijven aanwezig — alleen in één kolom i.p.v. twee. */}
        <KpiColumn>
          {/* Cycle #50 (Bram 2026-05-22) tile-redesign:
              - Verdiend  → vergroot via CSS (font + padding); keep kpi-tile-double
                            wrapper zodat breakdown-rows (PV/export/arbitrage) op
                            901-1500px naast hoofdwaarde komen.
              - Eigenverbruik PV → vervangen door EigenverbruikDuoTile (twee
                            percentages bidirectioneel naast elkaar: PV-dekking
                            van verbruik + PV-zelfverbruik ratio met surplus).
              - Morgen verwacht → vervangen door MorgenVerwachtTile met visuele
                            mini-sparkline van uurlijkse PV-forecast.
              - Batterij SOH + Status systeem → ongewijzigd (twee-cel double-tile).
              - Slimme actie → vervangen door SlimmeActieTile met huidige
                            engagement + tegenactie + spread-winst.
              Op mobile (≤900px) spannen Eigenverbruik / Morgen / Slim de volle
              breedte (zie kpi.css cycle #50 mobile-rules) zodat deadspace weg is. */}
          <div className="kpi-tile-double">
            <CombinedSavingsTile
              savingsTotal={savingsTotal}
              fallbackTodayEur={savings?.todayEur ?? null}
              fallbackBreakdown={savings ? {
                pvSelfUseEur: savings.todayPvSelfUseEur,
                pvExportEur: savings.todayPvExportEur,
                arbitrageEur: savings.todayArbitrageEur,
              } : null}
            />
          </div>
          <EigenverbruikDuoTile
            pvTotalKwh={pvTotalKwh}
            pvExportKwh={pvExportKwh}
            totalLoadKwh={totalLoadKwh}
            pvDirectToLoadKwh={pvDirectToLoadKwh}
          />
          {/* Erdotex: PV per dak van de meet-Pi's (remote_pv). Alleen als de
              snapshot de sleutel draagt; VJ/NS/HO renderen hier niets. */}
          {snap?.remote_pv?.sources?.map((s) => (
            <PvDakTile key={s.name} src={s} />
          ))}
          <MorgenVerwachtTile
            pvForecast={pvF}
            todayRealisedKwh={pvTodayRealisedKwh}
            todayProjectedKwh={todayKwhProjected}
            pvFillBadge={pvFillBadge}
          />
          {/* B3 (cycle #34): SOH + Status Systeem samengevoegd in één dubbele tegel.
              Links: batterij gezondheid (SOH %). Rechts: systeem-status + uptime.
              kpi-tile-double + combined-savings-tile activeert de side-by-side
              CSS-layout (zie kpi.css regel 95-105). */}
          <div className="kpi-tile-double">
            <div
              className="kpi-tile combined-savings-tile"
              style={{ ['--accent' as string]: snap?.soh != null ? '#06b6d4' : 'var(--text-tertiary)' } as import('react').CSSProperties}
            >
              <div className="combined-savings-main">
                <div className="kpi-label">Batterij SOH</div>
                <div className="kpi-value">
                  {snap?.soh != null
                    ? <>{snap.soh.toFixed(0)}<span className="unit"> %</span></>
                    : '—'}
                </div>
                <div className="kpi-sub">gezondheid</div>
              </div>
              <div className="combined-savings-breakdown">
                <div className="kpi-label">Status systeem</div>
                <div className="kpi-value small" style={{ color: status.color }}>
                  {status.label}
                </div>
                {status.sub != null && <div className="kpi-sub">{status.sub}</div>}
              </div>
            </div>
          </div>
          <SlimmeActieTile socForecast={socF} health={health} />
        </KpiColumn>

        <ControlsColumn
          /* Cycle #15 ronde 3: snapshot bevat nu ook ems_mode + ems_strategy
             (5s poll, identieke bron als /api/health). Fallback naar health
             voor backward-compat met oudere Pi's pre-b616df9. */
          emsMode={typeof (snap as Record<string, unknown> | null)?.ems_mode === 'string'
            ? ((snap as Record<string, unknown>).ems_mode as string)
            : typeof health?.ems_mode === 'string' ? health.ems_mode : undefined}
          emsStrategy={typeof (snap as Record<string, unknown> | null)?.ems_strategy === 'string'
            ? ((snap as Record<string, unknown>).ems_strategy as string)
            : typeof health?.ems_strategy === 'string' ? health.ems_strategy : undefined}
          emsEngagement={typeof (snap as Record<string, unknown> | null)?.ems_action_engagement === 'string'
            ? ((snap as Record<string, unknown>).ems_action_engagement as string)
            : typeof health?.ems_action_engagement === 'string' ? health.ems_action_engagement : undefined}
          /* P3-cy194-B BH-010: canonieke SOC-floor uit snapshot, primaire bron
             boven /api/reservation.effective_floor_pct. */
          floorCanonicalPct={snap?.floor_canonical_pct}
          /* installation.name — alleen om in de activerings-bevestiging te
             benoemen welke naam getypt moet worden. */
          siteName={install?.site_name ?? undefined}
          /* Punt 63: force-actie starten is admin-only, annuleren niet. */
          isAdmin={isAdmin}
        />
      </div>

      <ChartSwitcher
        apartPanels={[
          {
            title: 'Tarief & strategie · 48 uur',
            chart: (
              <ErrorBoundary label="Tarief & strategie">
                <PriceChart
                  buy={tl.buy}
                  sell={tl.sell}
                  bands={tl.bands}
                  manualBands={tl.manualBands}
                  now={now}
                  nowIdx={nowIdx}
                />
              </ErrorBoundary>
            ),
            legend: <PriceLegend bands={tl.bands} manual={tl.manualBands.length > 0} />,
          },
          {
            title: 'Energie-flow · 48 uur (history + forecast)',
            chart: (
              <ErrorBoundary label="Energie-flow">
                <TrendChart {...tl} now={now} reserveLock={reserveLock} nowIdx={nowIdx} />
              </ErrorBoundary>
            ),
            legend: <TrendLegend manual={tl.manualBands.length > 0} />,
          },
        ]}
        combinedPanel={{
          title: 'Tarief, energie-flow & strategie · 48 uur',
          chart: (
            <ErrorBoundary label="Tarief, energie-flow & strategie">
              <CombinedChart {...tl} now={now} reserveLock={reserveLock} nowIdx={nowIdx} />
            </ErrorBoundary>
          ),
          legend: <CombinedLegend bands={tl.bands} manual={tl.manualBands.length > 0} />,
        }}
      />
    </>
  );
}
